import React, { useEffect } from 'react';

import { CountdownDigits } from '@femtasy/ui';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/Button';
import Message from 'components/Message';

import { useAuth } from 'hooks/useAuth';

import {
  promoCountdownEndsAt,
  startPromoCountdown,
} from 'store/modules/promoCountdownBanner';
import ReduxState from 'store/ReduxState';

import { useUrls } from 'constants/urls';

import { usePromoCountdownBannerVisibility } from './usePromoCountdownBannerVisibility';

const digitClassName =
  'font-zigzag text-primary text-2xl uppercase leading-10';

const PromoCountdownBanner = () => {
  const dispatch = useDispatch();

  const { hasPremiumAccess, isAuthenticated, user } = useAuth();
  const { pricingPage } = useUrls();

  const endsAt = useSelector<ReduxState, number | undefined>(
    promoCountdownEndsAt,
  );
  const { isVisible, timeLeft } = usePromoCountdownBannerVisibility();

  const isAuthProfilePending = isAuthenticated && !user;

  useEffect(() => {
    if (isAuthProfilePending) {
      return;
    }

    const hasLapsed = endsAt !== undefined && endsAt <= Date.now();

    if (!hasPremiumAccess && (endsAt === undefined || hasLapsed)) {
      dispatch(startPromoCountdown({ now: Date.now() }));
    }
  }, [dispatch, endsAt, hasPremiumAccess, isAuthProfilePending]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="flex flex-col items-center gap-1 bg-secondary p-4 md:mx-auto md:max-w-[600px]"
      data-testid="promo-countdown-banner"
    >
      <CountdownDigits
        digitClassName={digitClassName}
        showHours={false}
        timeLeft={timeLeft}
      />
      <p className="text-center text-base text-dark-body">
        <Message
          message={{ id: 'components.PromoCountdownBanner.offerText' }}
        />
      </p>
      <Button
        linkTo={isAuthenticated ? pricingPage : '/sign-up'}
        title={{ id: 'components.PromoCountdownBanner.cta' }}
        variant="primary"
      />
    </div>
  );
};

export default PromoCountdownBanner;
